// Runtime config reader.
// At container startup, entrypoint.sh writes env vars from the K8s Secret into
// /app/build/env-config.js which sets window.__runtimeCfg__. That script runs
// before the React bundle. This module reads the values into a private closure
// and immediately deletes the global so it is not trivially inspectable in DevTools.
// Falls back to process.env (build-time values) for local development.

declare global {
  interface Window {
    __runtimeCfg__?: Record<string, string | undefined>;
  }
}

// True for a value that is still an unsubstituted envsubst placeholder, e.g.
// "${REACT_APP_KEYCLOAK_URL}". This happens in local dev (`npm start`) because
// nothing runs entrypoint.sh's envsubst over public/env-config.js — the browser
// receives the literal "${VAR}" strings. Left as-is they are *truthy*, so getEnv
// would return them instead of falling back to the build-time process.env values,
// producing bogus relative URLs (e.g. a Keycloak token POST to localhost:3000 that
// returns index.html → "Unexpected token '<'"). In prod these are always real
// values, so this predicate never matches there.
const isUnsubstitutedPlaceholder = (v: string | undefined): boolean =>
  typeof v === 'string' && /^\$\{[^}]*\}$/.test(v.trim());

// Snapshot runtime config into a private module-scope variable, dropping any
// unsubstituted placeholders so getEnv() falls back to process.env in local dev.
const _cfg: Record<string, string | undefined> = Object.fromEntries(
  Object.entries({ ...window.__runtimeCfg__ }).filter(([, v]) => !isUnsubstitutedPlaceholder(v)),
);

// Remove the global after consuming it so it doesn't linger as an inspectable named object
try {
  delete window.__runtimeCfg__;
} catch {
  // Non-configurable in some environments — ignore
}

// A value is "unset" if it's empty or an unsubstituted envsubst placeholder
// (e.g. "${REACT_APP_KEYCLOAK_URL}"). Placeholders leak through in local dev,
// where nothing runs envsubst over public/env-config.js — and because they are
// truthy strings they would otherwise shadow the build-time process.env values
// that `npm start` loads from .env.local, blanking the app on boot.
function isUnset(value: string | undefined): boolean {
  return !value || /^\$\{[A-Z0-9_]+\}$/.test(value);
}

export function getEnv(key: string): string | undefined {
  // Runtime injection takes precedence; fall back to build-time process.env
  const runtime = _cfg[key];
  if (!isUnset(runtime)) return runtime;
  return (process.env as Record<string, string | undefined>)[key];
}
